<script setup lang="ts">
import { computed, nextTick, onMounted, onScopeDispose, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDialogFocus } from '~/composables/useDialogFocus';

const props = defineProps<{ anchor: HTMLElement | null }>();
const emit = defineEmits(['close', 'logout']);
const { t } = useI18n();
const open = ref(true);
const languageOpen = ref(false);
const position = ref({ top: 16, left: 16 });
let disposed = false;
const handleClose = () => { open.value = false; emit('close'); };
const handleLogout = () => { emit('logout'); };
const { dialog, closeButton, handleKeydown } = useDialogFocus(open, handleClose);
const panelStyle = computed(() => ({ top: position.value.top + 'px', left: position.value.left + 'px' }));

// Teleport avoids clipping by the sidebar's scroll area. Anchor within the viewport.
const positionMenu = () => {
  if (!dialog.value || !props.anchor) return;
  const anchor = props.anchor.getBoundingClientRect();
  const panel = dialog.value.getBoundingClientRect();
  const margin = 16;
  const above = anchor.top - panel.height - 8;
  const top = above >= margin ? above : anchor.bottom + 8;
  position.value = {
    left: Math.max(margin, Math.min(anchor.left, window.innerWidth - panel.width - margin)),
    top: Math.max(margin, Math.min(top, window.innerHeight - panel.height - margin)),
  };
};
const lockedAreas: Array<{ element: HTMLElement; overflow: string; inert: boolean }> = [];
onMounted(async () => {
  for (const selector of ['[data-musicmint-page-scroll]', '.nav-scrollable-content']) {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) continue;
    lockedAreas.push({ element, overflow: element.style.overflow, inert: element.inert });
    element.style.overflow = 'hidden';
    if (selector === '[data-musicmint-page-scroll]') element.inert = true;
  }
  await nextTick();
  if (disposed || !open.value) return;
  positionMenu();
  window.addEventListener('resize', positionMenu);
});
onScopeDispose(() => {
  disposed = true;
  if (typeof window !== 'undefined') window.removeEventListener('resize', positionMenu);
  for (const { element, overflow, inert } of lockedAreas) {
    element.style.overflow = overflow;
    element.inert = inert;
  }
});
</script>

<template>
  <Teleport to="body">
    <div class="settings-overlay" @click.self="handleClose" @keydown="handleKeydown">
      <div ref="dialog" role="dialog" :aria-modal="languageOpen ? undefined : true" :aria-label="t('settings.accountMenu')"
        :aria-hidden="languageOpen || undefined" :inert="languageOpen" tabindex="-1" class="settings-popup" :style="panelStyle">
        <div class="settings-heading">
          <h3 class="min-w-0 break-words text-sm font-medium">{{ t('settings.accountMenu') }}</h3>
          <button ref="closeButton" type="button" :aria-label="t('common.close')" @click="handleClose" class="musicmint-dismiss">
            <span class="i-lucide-x h-4 w-4" aria-hidden="true"></span>
          </button>
        </div>
        <div class="settings-content">
          <NuxtLink to="/billing" class="item-content" @click="handleClose">
            <span class="i-lucide-credit-card text-xl shrink-0" aria-hidden="true"></span>
            <span class="min-w-0 break-words">{{ t('billing.membershipEntry') }}</span>
          </NuxtLink>
          <LanguageSwitcher menu-item @open-change="languageOpen = $event" @changed="handleClose" />
          <button type="button" class="item-content text-red-500" @click="handleLogout">
            <span class="i-lucide-log-out text-xl shrink-0" aria-hidden="true"></span>
            <span class="min-w-0 break-words">{{ t('settings.logout') }}</span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.settings-overlay {
  @apply fixed inset-0 z-[200];
}
.settings-popup {
  @apply fixed flex flex-col w-60 overflow-hidden rounded-xl shadow-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700;
  max-width: calc(100vw - 32px);
  max-height: calc(100dvh - 32px);
}
.settings-heading {
  @apply flex shrink-0 items-center justify-between gap-2 py-1 pl-4 pr-2 border-b border-gray-200 dark:border-gray-700;
}
.settings-content {
  @apply min-h-0 overflow-y-auto overscroll-contain py-2;
}
.item-content {
  @apply flex min-h-[44px] w-full items-center gap-3 px-4 py-3 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700;
  @apply focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-300;
}
</style>
